import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Baby, Save, Loader2, Check } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/App";
import { useLanguage } from "@/i18n/LanguageContext";
import { useTheme } from "@/theme/ThemeContext";

/**
 * PerinatalProfilePane — compact Profile UI for users to set their
 * perinatal stage + chronic conditions. Persists to the user document
 * (``user.profile.perinatal``) via the existing /api/auth/me PATCH or
 * a dedicated /api/perinatal-dil/profile POST.
 */
export default function PerinatalProfilePane() {
  const { API, user } = useAuth();
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const bg = language === "bg";

  const [stages, setStages] = useState([]);
  const [conditionsCatalog, setConditionsCatalog] = useState([]);
  const [stage, setStage] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [saving, setSaving] = useState(false);
  const [savedOk, setSavedOk] = useState(false);

  const fetchOptions = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/perinatal-dil/options`);
      setStages(data.stages || []);
      setConditionsCatalog(data.conditions || []);
    } catch (_e) { /* silent */ }
  }, [API]);

  const fetchUserPerinatal = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/auth/me`);
      const p = data?.profile?.perinatal || data?.perinatal || {};
      setStage(p.stage || "");
      setSelected(new Set(p.conditions || []));
    } catch (_e) { /* silent */ }
  }, [API]);

  useEffect(() => {
    fetchOptions();
    fetchUserPerinatal();
  }, [fetchOptions, fetchUserPerinatal]);

  const save = async () => {
    setSaving(true);
    setSavedOk(false);
    try {
      await axios.patch(`${API}/auth/me`, {
        profile: {
          perinatal: {
            stage:           stage || null,
            conditions:      [...selected],
            lactation_mode:  stage?.startsWith("lactation_") ? stage.replace("lactation_", "") : null,
          },
        },
      });
      toast.success(bg ? "Запазено" : "Saved");
      setSavedOk(true);
      setTimeout(() => setSavedOk(false), 2500);
    } catch (e) {
      toast.error(bg ? "Грешка при запис" : "Save failed");
    } finally { setSaving(false); }
  };

  const toggleCond = (key) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className={`rounded-2xl p-5 ${
        isDark
          ? "bg-slate-900/60 border border-slate-800"
          : "bg-white border border-slate-200"
      }`}
      data-testid="perinatal-profile-pane"
    >
      <div className="flex items-center gap-2 mb-4">
        <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${
          isDark ? "bg-pink-500/30" : "bg-pink-200"
        }`}>
          <Baby className={`w-4 h-4 ${isDark ? "text-pink-200" : "text-pink-700"}`} />
        </div>
        <div>
          <h3 className="text-sm font-bold">
            {bg ? "Перинатален профил" : "Perinatal profile"}
          </h3>
          <p className="text-[11px] opacity-60">
            {bg
              ? "Активира персонализиран DIL таван за бременност / кърмене"
              : "Activates a personalised DIL ceiling for pregnancy / lactation"}
          </p>
        </div>
      </div>

      {/* Stage selector */}
      <label className="block text-[11px] font-bold uppercase tracking-wider opacity-70 mb-1">
        {bg ? "Етап" : "Stage"}
      </label>
      <select
        value={stage}
        onChange={(e) => setStage(e.target.value)}
        className={`w-full p-2 rounded-lg text-sm mb-4 ${
          isDark
            ? "bg-slate-800 border border-slate-700 text-white"
            : "bg-white border border-slate-300"
        }`}
        data-testid="perinatal-stage-select"
      >
        <option value="">{bg ? "— Не съм в перинатален период —" : "— Not in a perinatal window —"}</option>
        {stages.map((s) => (
          <option key={s.key} value={s.key}>
            {bg ? s.label_bg : s.label_en}
            {s.extra_kcal_per_day > 0 ? ` (+${s.extra_kcal_per_day} kcal)` : ""}
          </option>
        ))}
      </select>

      {/* Conditions */}
      <label className="block text-[11px] font-bold uppercase tracking-wider opacity-70 mb-2">
        {bg ? "Хронични състояния" : "Chronic conditions"}
      </label>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 mb-4">
        {conditionsCatalog.map((c) => {
          const active = selected.has(c.key);
          return (
            <button
              key={c.key}
              type="button"
              onClick={() => toggleCond(c.key)}
              data-testid={`perinatal-cond-${c.key}`}
              title={c.rationale_bg}
              className={`text-left text-xs px-2.5 py-1.5 rounded-lg border transition-colors ${
                active
                  ? "bg-pink-500 text-white border-pink-600"
                  : isDark
                    ? "bg-slate-800 border-slate-700 hover:border-pink-500/50"
                    : "bg-white border-slate-200 hover:border-pink-300"
              }`}
            >
              <div className="flex items-center justify-between gap-2">
                <span className="truncate">{bg ? c.label_bg : c.label_en}</span>
                <span className={`text-[10px] font-mono ${active ? "text-pink-100" : "opacity-50"}`}>
                  ×{c.factor}
                </span>
              </div>
            </button>
          );
        })}
      </div>

      <button
        onClick={save}
        disabled={saving}
        className={`w-full px-4 py-2 rounded-lg font-semibold text-sm flex items-center justify-center gap-2 transition-all ${
          savedOk
            ? "bg-emerald-500 text-white"
            : "bg-pink-600 hover:bg-pink-700 text-white"
        } disabled:opacity-60`}
        data-testid="perinatal-save"
      >
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (savedOk ? <Check className="w-4 h-4" /> : <Save className="w-4 h-4" />)}
        {savedOk ? (bg ? "Запазено" : "Saved") : (bg ? "Запази профил" : "Save profile")}
      </button>

      <p className="text-[10px] opacity-50 italic mt-3">
        {bg
          ? "Образователен инструмент. Обсъдете промяна на режима с гинеколог/ендокринолог."
          : "Educational tool. Discuss any regimen change with your obstetrician / endocrinologist."}
      </p>
    </motion.div>
  );
}
